/**
 * SA mobile network detection from MSISDN prefix.
 * Mirrors backend `utils/msisdn.js` — keep in sync when prefix ranges change.
 */

export type SaMobileNetwork = 'vodacom' | 'mtn' | 'cellc' | 'telkom';

const NETWORK_DISPLAY: Record<SaMobileNetwork, string> = {
  vodacom: 'Vodacom',
  mtn: 'MTN',
  cellc: 'CellC',
  telkom: 'Telkom',
};

const PINLESS_NETWORKS = new Set<string>(['vodacom', 'mtn', 'cellc', 'telkom']);

/** Normalise network labels from metadata, products, or user input. */
export function normalizeNetworkName(network: string | null | undefined): string {
  if (!network) return '';
  const normalized = String(network).toLowerCase().trim();
  const networkMap: Record<string, string> = {
    vodacom: 'vodacom',
    mtn: 'mtn',
    cellc: 'cellc',
    'cell c': 'cellc',
    telkom: 'telkom',
    'telkom mobile': 'telkom',
    eeziairtime: 'eeziairtime',
    'eezi airtime': 'eeziairtime',
    global: 'global',
    'global-airtime': 'global',
    'global airtime': 'global',
    'global data': 'global',
  };
  return networkMap[normalized] || normalized;
}

/** Detect pinless network from MSISDN prefix, or null when unknown / non-mobile. */
export function getNetworkFromMsisdn(msisdn: string | null | undefined): SaMobileNetwork | null {
  if (!msisdn || typeof msisdn !== 'string') return null;
  if (msisdn.startsWith('NON_MSI_')) return null;

  const digits = msisdn.replace(/\D/g, '');
  if (!digits || digits.length < 3) return null;

  let prefix: string;
  if (digits.startsWith('27') && digits.length >= 11) {
    prefix = `0${digits.slice(2, 5)}`;
  } else if (digits.startsWith('0') && digits.length >= 10) {
    prefix = digits.substring(0, 3);
  } else {
    return null;
  }

  if (prefix === '082' || prefix === '084') return 'vodacom';
  if (prefix === '083' || prefix === '081' || prefix === '060' || prefix === '061' || prefix === '073') return 'mtn';
  if (prefix === '076' || prefix === '074') return 'cellc';
  // 068 confirmed on production MobileMart Telkom pinless (May 2026)
  if (prefix === '085' || prefix === '087' || prefix === '068') return 'telkom';

  return null;
}

export function getNetworkDisplayName(network: string | null | undefined): string {
  const key = normalizeNetworkName(network) as SaMobileNetwork;
  return NETWORK_DISPLAY[key] || (network ? String(network) : 'Unknown');
}

/** Extract normalised pinless network from a catalog product. */
export function extractProductNetwork(product: {
  network?: string;
  provider?: string;
  name?: string;
  rawName?: string;
}): string {
  if (product.network) return normalizeNetworkName(product.network);
  if (product.provider) return normalizeNetworkName(product.provider);

  const name = (product.name || product.rawName || '').toLowerCase();
  if (name.includes('vodacom')) return 'vodacom';
  if (name.includes('mtn')) return 'mtn';
  if (name.includes('cell c') || name.includes('cellc')) return 'cellc';
  if (name.includes('telkom')) return 'telkom';
  if (name.includes('eezi')) return 'eeziairtime';
  if (name.includes('global')) return 'global';
  return '';
}

export function isPinlessNetworkProduct(productNetwork: string): boolean {
  return PINLESS_NETWORKS.has(normalizeNetworkName(productNetwork));
}

export interface MsisdnNetworkMismatch {
  mismatch: boolean;
  detectedNetwork: SaMobileNetwork | null;
  productNetwork: string;
  message?: string;
}

/** True when MSISDN prefix identifies a different pinless network than the product. */
export function getMsisdnNetworkMismatch(
  msisdn: string | null | undefined,
  productNetwork: string
): MsisdnNetworkMismatch {
  const detectedNetwork = getNetworkFromMsisdn(msisdn);
  const normalizedProduct = normalizeNetworkName(productNetwork);

  if (!detectedNetwork || !isPinlessNetworkProduct(normalizedProduct)) {
    return { mismatch: false, detectedNetwork, productNetwork: normalizedProduct };
  }

  if (detectedNetwork !== normalizedProduct) {
    return {
      mismatch: true,
      detectedNetwork,
      productNetwork: normalizedProduct,
      message:
        `This mobile number looks like ${getNetworkDisplayName(detectedNetwork)}, not ${getNetworkDisplayName(normalizedProduct)}. ` +
        'Pinless airtime and data must match the number\'s network. Please choose the correct network product or update the recipient.',
    };
  }

  return { mismatch: false, detectedNetwork, productNetwork: normalizedProduct };
}

/** Banner when saved beneficiary network label disagrees with MSISDN prefix. */
export function getBeneficiaryNetworkLabelWarning(
  msisdn: string | null | undefined,
  savedNetwork: string | null | undefined
): string | null {
  const detected = getNetworkFromMsisdn(msisdn);
  const saved = normalizeNetworkName(savedNetwork);
  if (!detected || !saved || !isPinlessNetworkProduct(saved)) return null;
  if (detected === saved) return null;

  return (
    `This number (${formatLocalMsisdn(msisdn)}) appears to be ${getNetworkDisplayName(detected)}, ` +
    `but the recipient is saved as ${getNetworkDisplayName(saved)}. Showing ${getNetworkDisplayName(detected)} products only.`
  );
}

function formatLocalMsisdn(msisdn: string | null | undefined): string {
  if (!msisdn) return '';
  const digits = msisdn.replace(/\D/g, '');
  if (digits.startsWith('27') && digits.length >= 11) return `0${digits.slice(2)}`;
  if (digits.startsWith('0')) return digits;
  return msisdn;
}
